import SavedSlide from './SavedSlide.jsx';
import ReportIllustration from './ReportIllustration.jsx';
import Slide, { SLIDE_W, SLIDE_H } from './Slide.jsx';
import PageBody from './PageBody.jsx';
import { strokePoints } from '../../store/illustrations.js';
import { money } from './format.js';

// The pages the prospect leaves with.
//
// Each is the slide the meeting showed, rendered by the same component at the
// same fixed size, with any ink drawn on it in the room laid over it exactly
// where it was made. A page with a drawing and a page without have the same
// margins, the same white paper and the same footer, because they are the
// same page (Jimmy, 2026-09-29).
//
// After them come the whiteboards, and any drawing made over figures that
// have since changed, printed from the copy of the page it was drawn on.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (m) return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const STEP_TITLES = {
  today: 'Today',
  goals: 'Goals',
  whiteboard: 'Illustration',
  cash: 'Cash flow',
  tax: 'Where your money sits',
  investments: 'Investments',
  costs: 'What it costs',
  protection: 'Protection',
  next: 'What planning does next',
  // Moments from before 2026-09-29.
  start: 'Where you stand',
  noticed: 'What we noticed',
  decisions: 'Decisions and next steps',
};

function Foot({ page, left, demo }) {
  return (
    <div className="conv-page-foot">
      <span>{demo ? 'Sample data, not a client assessment' : left}</span>
      <span>{`${page.page} / ${page.of}`}</span>
    </div>
  );
}

function SlidePage({ page, demo, footerLeft, asOf }) {
  return (
    <article className="conv-page conv-page-slide" data-step={page.step} data-board={page.board || undefined}>
      <Slide strokes={page.ink?.strokes || []} maxScale={1}>
        <PageBody step={page.step} page={page.model} asOf={asOf} />
      </Slide>
      {/* A whiteboard has no title of its own, so its footer says what it is. */}
      <Foot page={page} left={page.step === 'whiteboard' ? 'Illustration from our meeting' : footerLeft} demo={demo && page.step !== 'whiteboard'} />
    </article>
  );
}

// A drawing made over figures that have since been corrected, printed from
// the copy of the page it was made on, on the same paper as every other page.
function EarlierPage({ page, demo }) {
  const when = page.updated ? fmtDate(page.updated.slice(0, 10)) : '';
  return (
    <article className="conv-page conv-page-slide is-earlier">
      <div className="conv-slide-frame is-static" style={{ aspectRatio: `${SLIDE_W} / ${SLIDE_H}` }}>
        <SavedSlide
          capture={page.capture}
          strokes={page.strokes}
          fill="#fff"
          align="xMinYMin meet"
          label={`${STEP_TITLES[page.step] || 'Page'}, as drawn on${when ? ` ${when}` : ''}`}
        />
      </div>
      <Foot
        page={page}
        demo={demo}
        left={`${STEP_TITLES[page.step] || 'This page'} as drawn on${when ? ` ${when}` : ' earlier'}, before its figures were updated`}
      />
    </article>
  );
}

// The drawing, over the figures it was drawn about, for whiteboards and for
// drawings from before the slide layout.
function BasisPanel({ basis }) {
  if (!basis) return null;
  const line = (label, value) => (value === null || value === undefined
    ? null
    : <div key={label}><span>{label}</span><strong>{typeof value === 'number' ? money(value) : value}</strong></div>);
  return (
    <div className="conv-page-basis">
      {line('Annual spending', basis.spending)}
      {line('Annual income', basis.income)}
      {line('Retirement year', basis.retirement === null || basis.retirement === undefined ? null : String(basis.retirement))}
      {line('Assets', basis.assets)}
      {line('Tax-deferred', basis.pools?.taxDeferred)}
      {line('Roth', basis.pools?.roth)}
      {line('Taxable', basis.pools?.taxable)}
    </div>
  );
}

// The drawing is cropped to its own bounds rather than shown in the page
// coordinates it happened to be drawn at, which keeps a whiteboard sketch
// from being stranded in white space.
function inkBox(strokes) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const s of strokes) {
    for (const [x, y] of s.p || []) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 1, h: 0.5 };
  const pad = Math.max(0.02, (maxX - minX) * 0.06, (maxY - minY) * 0.06);
  return {
    x: minX - pad,
    y: minY - pad,
    w: Math.max(0.05, maxX - minX + pad * 2),
    h: Math.max(0.05, maxY - minY + pad * 2),
  };
}

function IllustrationPage({ page, demo, footerLeft }) {
  const box = inkBox(page.strokes);
  const label = page.title?.replace(/goal[ -]sheet/gi, 'Goals')
    || (page.drawingKind === 'board' ? 'Whiteboard' : STEP_TITLES[page.step] || 'The conversation');

  // Drawings from before the saved slide carried the whole page (layout
  // versions under 3) are rebuilt from their saved figures; later ones print
  // the slide as it was captured.
  const legacy = !(page.basis?.layoutVersion >= 3);
  const report = legacy && page.capture && page.basis?.pools && (page.step === 'goals' || (page.step === 'noticed' && (page.board === 'tax' || (page.board === 'portfolio' && page.basis.analysis?.availability?.state === 'ready'))));
  const saved = page.capture ? <SavedSlide capture={page.capture} strokes={page.strokes} paper={Boolean(report)} fill="#fff" align="xMidYMin meet" label={`${label}, saved slide and annotations`}/> : null;
  return (
    <article className={`conv-page conv-page-art${page.capture ? ' has-slide' : ''}`}>
      {!page.capture && <><div className="conv-eyebrow">Advisor illustration</div><h2>{label}</h2></>}
      {!page.capture && page.drawingKind !== 'board' && <><BasisPanel basis={page.basis}/><p className="conv-page-note">Legacy drawing: the original slide layout was not saved. Ink is shown separately; its placement over the figures cannot be reproduced.</p></>}
      <div className="conv-page-ink">
        {page.capture ? (report ? <ReportIllustration page={page} fallback={saved}/> : saved) : <svg
          viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`${label}, as drawn`}
        >
          {page.strokes.map((s, i) => (
            <polyline
              key={i}
              points={strokePoints(s, 1)}
              fill="none"
              stroke={s.c}
              strokeWidth={Math.max(box.w, box.h) * 0.006}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>}
      </div>
      <Foot page={page} left={footerLeft} demo={demo} />
    </article>
  );
}

/**
 * The pages, and nothing above them. Printing is a small button in the header
 * beside the mode switch (Jimmy, 2026-10-05). `blocked` is whatever stops the
 * packet going out at all; only then is anything said here, so the reason a
 * disabled button is disabled is never hidden. What the packet merely leaves
 * out, it says on its own pages.
 */
export default function TakeawayView({ takeaway, blocked = [] }) {
  return (
    <>
      {/* Scope the default page to this mounted view. Chromium can otherwise
          retain the classic report's portrait sheet for the outer page context. */}
      <style>{'@media print { @page { size: letter landscape; margin: 8mm; } }'}</style>
      {blocked.length ? (
        <div className="conv-takeaway-note no-print" role="status">
          {blocked.map(l => <p key={l.key}>{l.text}</p>)}
        </div>
      ) : null}

      <div className="conv-pages">
        {takeaway.pages.map(page => {
          const shared = { page, demo: takeaway.demo, footerLeft: takeaway.footerLeft };
          if (page.kind === 'slide') return <SlidePage key={`${page.step}:${page.board || ''}`} {...shared} asOf={takeaway.asOf} />;
          if (page.kind === 'earlier') return <EarlierPage key={page.id} {...shared} />;
          return <IllustrationPage key={page.id} {...shared} />;
        })}
      </div>
    </>
  );
}
